import { useRef, useState } from 'react';
import { LeftArrowIcon } from '../../components/icons';
import { InlineSpinner } from '../../components/common/spinner';
import { usePaymentMethod } from '../../hooks/api/useStripe';
import { FormBlock } from '../../components/modals/settings-modal/form-block';
import { PageHeader } from '../../components/modals/settings-modal/page-header';
import { UserPlan } from '../../components/common/user-plan/user-plan';
import { AddPaymentForm } from '../../components/common/payment/add-payment-form';
import { PoweredByStripe } from '../../components/common/payment/powered-by-stripe';
import { PaymentMethod as PaymentMethodCard } from '../../components/common/payment/payment-method';
import Toast from '../../components/common/pop-up-message';
import useAuth from '../../hooks/utils/useAuth';

export const PaymentMethod = () => {
  const toastNotificationContainerRef = useRef<HTMLDivElement>(null);
  const [showUpdateCardForm, setShowUpdateCardForm] = useState<boolean>(false);
  const { user, isLoading: userIsLoading } = useAuth();
  const { data: { payment_method } = {}, isLoading: isPaymentMethodLoading } = usePaymentMethod(user?.id);

  if (userIsLoading || isPaymentMethodLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <InlineSpinner />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div ref={toastNotificationContainerRef} />
      {showUpdateCardForm && (
        <button
          onClick={() => setShowUpdateCardForm(false)}
          className="text-primary font-open-sans mb-6 flex gap-2 text-base font-medium"
        >
          <LeftArrowIcon />
          Back
        </button>
      )}
      <PageHeader header={showUpdateCardForm ? 'Update card details' : 'Payment'} />
      {!!user && (
        <div className="mt-6 space-y-4">
          {!showUpdateCardForm && (
            <FormBlock>
              <UserPlan user={user} />
            </FormBlock>
          )}
          {!payment_method?.card || showUpdateCardForm ? (
            <AddPaymentForm
              className="space-y-4 rounded-lg border border-neutral-200 bg-white px-4 py-5"
              bottomActionButton={({ isSubmitting }) => (
                <div className="mt-4 flex items-center justify-between">
                  <PoweredByStripe />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="text-primary font-open-sans bg-button-secondary hover:bg-button-secondary-hover inline-flex h-10 items-center justify-center gap-1.5 rounded-full px-6 py-2.5 text-base font-medium leading-5 transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? 'Updating...' : 'Update details'}
                  </button>
                </div>
              )}
              onSuccessAddPayment={() => {
                setShowUpdateCardForm(false);
                Toast.success(
                  'You have successfully updated your payment details',
                  'Payment details updated!',
                  toastNotificationContainerRef?.current,
                );
              }}
              onErrorAddPayment={() => {
                Toast.error(
                  'Your payment method has failed to process.  Confirm your payment info is correct and try again.',
                  'Payment Failed',
                  toastNotificationContainerRef?.current,
                );
              }}
            />
          ) : (
            <FormBlock
              header="Payment"
              subHeader="Add your payment details below in order to complete the purchase of your plan."
            >
              <PaymentMethodCard
                className="rounded-lg border border-neutral-200 p-4"
                onUpdateButtonClick={() => setShowUpdateCardForm(true)}
                onSuccessRemovePayment={() => {
                  Toast.success(
                    'You have successfully removed your payment details',
                    'Payment details updated!',
                    toastNotificationContainerRef?.current,
                  );
                }}
              />
            </FormBlock>
          )}
        </div>
      )}
    </div>
  );
};
